import {
  SourcesApi,
  CitationsApi,
  type SourceResponse,
  type SourceListResponse,
  type CreateSourceRequest,
  type UpdateSourceRequest,
  type CitationResponse,
  type CitationListResponse,
  type CreateCitationRequest,
  type UpdateCitationRequest,
  type CitationLinkResponse,
  type CitationLinkListResponse,
  type CreateCitationLinkRequest
} from 'we-tree-api-client/v2';
import type { Configuration } from 'we-tree-api-client/v2';
import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export type {
  SourceResponse,
  SourceListResponse,
  CreateSourceRequest,
  UpdateSourceRequest,
  CitationResponse,
  CitationListResponse,
  CreateCitationRequest,
  UpdateCitationRequest,
  CitationLinkResponse,
  CitationLinkListResponse,
  CreateCitationLinkRequest
};

function createSourcesApi(config: Configuration) {
  return new SourcesApi(config);
}

function createCitationsApi(config: Configuration) {
  return new CitationsApi(config);
}

/**
 * Wrapped sources API (sources + citations) with standardized error handling
 */
class WrappedSourcesApi {
  async listSources(
    familyId: string,
    params?: { type?: string; page?: number; pageSize?: number }
  ): Promise<ApiResult<SourceListResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) =>
      api.listSources(familyId, params?.type, params?.page, params?.pageSize)
    );
  }

  async getSource(familyId: string, sourceId: string): Promise<ApiResult<SourceResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) => api.getSource(familyId, sourceId));
  }

  async createSource(familyId: string, body: CreateSourceRequest): Promise<ApiResult<SourceResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) => api.createSource(familyId, body));
  }

  async updateSource(
    familyId: string,
    sourceId: string,
    body: UpdateSourceRequest
  ): Promise<ApiResult<SourceResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) => api.updateSource(familyId, sourceId, body));
  }

  async deleteSource(familyId: string, sourceId: string): Promise<ApiResult<void>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) => api.deleteSource(familyId, sourceId));
  }

  async listCitationsBySource(
    familyId: string,
    sourceId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<ApiResult<CitationListResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) =>
      api.listCitationsBySource(familyId, sourceId, params?.page, params?.pageSize)
    );
  }

  async createCitation(
    familyId: string,
    sourceId: string,
    body: CreateCitationRequest
  ): Promise<ApiResult<CitationResponse>> {
    return withApiSetup(createSourcesApi, getApiConfiguration, (api) => api.createCitation(familyId, sourceId, body));
  }
}

/**
 * Wrapped citations API (citations + citation links) with standardized error handling
 */
class WrappedCitationsApi {
  async listCitations(
    familyId: string,
    params?: { sourceId?: string; page?: number; pageSize?: number }
  ): Promise<ApiResult<CitationListResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) =>
      api.listCitations(familyId, params?.sourceId, params?.page, params?.pageSize)
    );
  }

  async getCitation(familyId: string, citationId: string): Promise<ApiResult<CitationResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) => api.getCitation(familyId, citationId));
  }

  async updateCitation(
    familyId: string,
    citationId: string,
    body: UpdateCitationRequest
  ): Promise<ApiResult<CitationResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) =>
      api.updateCitation(familyId, citationId, body)
    );
  }

  async deleteCitation(familyId: string, citationId: string): Promise<ApiResult<void>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) => api.deleteCitation(familyId, citationId));
  }

  async listCitationLinks(familyId: string, citationId: string): Promise<ApiResult<CitationLinkListResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) => api.listCitationLinks(familyId, citationId));
  }

  async listCitationLinksByTarget(
    familyId: string,
    targetType: string,
    targetId: string
  ): Promise<ApiResult<CitationLinkListResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) =>
      api.listCitationLinksByTarget(familyId, targetType, targetId)
    );
  }

  async createCitationLink(
    familyId: string,
    citationId: string,
    body: CreateCitationLinkRequest
  ): Promise<ApiResult<CitationLinkResponse>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) =>
      api.createCitationLink(familyId, citationId, body)
    );
  }

  async deleteCitationLink(familyId: string, citationId: string, linkId: string): Promise<ApiResult<void>> {
    return withApiSetup(createCitationsApi, getApiConfiguration, (api) =>
      api.deleteCitationLink(familyId, citationId, linkId)
    );
  }
}

export const sourcesApi = new WrappedSourcesApi();
export const citationsApi = new WrappedCitationsApi();
